import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio as MuiRadio,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Home,
  LayoutDashboard,
  Shield,
  RefreshCw,
  FileText,
  Clock,
  Eye,
  FileCheck2,
  Sparkles,
  Brain,
  Bug,
  Key,
  Container,
  Gauge,
  Target,
  CheckCircle2,
  XCircle,
  Radio,
  ExternalLink,
  Star,
  Play,
  Layers,
  Loader,
  Download,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  GitBranch,
  Search,
  ShieldQuestion,
} from 'lucide-react';
import { Collapse } from '@mui/material';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import NerdSpinner from '@/components/NerdSpinner';
import { AppInsight, RepoProgress, RepoSecurityResult } from '@/types';
import { useProjectName } from '@/app/layout/ProjectNameContext';
import { useScan } from '@/lib/ScanContext';
import { getExtractionStatus, getExtractionStatuses, triggerOnDemandScan, type ExtractionStatuses } from '@/lib/api/scanService';
import {
  AiKpiScore,
  AiKpiRepoScore,
  KPI_TOOL_ORDER,
  findToolDef,
  analyzeAiKpi,
  getAiKpiScore,
  triggerAiScan as triggerAiScanApi,
  getAiScanStatus as getAiScanStatusApi,
  type ScanProgress,
} from '@/lib/api/aiKpiService';
import { fetchManagementAccessEntries } from '@/app/onboarding-secops/api';
import {
  triggerScanPipeline,
  getPipelineStatus as fetchPipelineStatus,
  pollPipelineStatus,
  PipelineStatus,
} from '@/lib/api/pipelineService';
import { getToolReportDetail, type ToolReportDetail } from '@/lib/api/toolReportService';
import PolarisRepoTab from '../PolarisRepoTab';
import {
  ScoreWidget,
  ToolContributors,
  SectionCard,
  ScoreTrendChart,
  ProjectStatusBadge,
  SectionSkeleton,
} from './DashboardWidgets';
import { SonarQubeTab, BlackDuckTab, GitLeaksTab, TrivyTab, DeltaAiKpisTab } from './ToolTabs';
import { CrossAiInsights, AiInsight } from './CrossAiInsights';
import { DeepAiAnalysis } from './DeepAiAnalysis';
import { EpaFindingAccordion } from '../EpaFindingAccordion';
import { SecurityTaskDialog } from '../SecurityTaskDialog';
import SecurityProfileDisplay from '../SecurityProfileDisplay';
import { exportEpaReportPdf } from '@/lib/exportEpaReport';
import type { EpaFinding } from '@/types';
import { AiPrioritizedIssues, PrioritizedIssue } from './AiPrioritizedIssues';
import { StyledBreadcrumb } from '../constants';
import WorkflowGeneratorDialog from '../WorkflowGeneratorDialog';
import RepoSelectionDialog from '../RepoSelectionDialog';
import ScanEngineDialog, { type AiEngine } from '../ScanEngineDialog';

// ── Helpers ───────────────────────────────────────────────────

/**
 * Call /ai-kpi/analyze; on 504/502 (API Gateway timeout while Lambda continues),
 * poll /ai-kpi-scores every 5s for up to 60s waiting for the Lambda to finish
 * storing results, then update state with whatever arrives.
 */
async function analyzeWithFallbackPoll(
  payload: Parameters<typeof analyzeAiKpi>[0],
  setScore: (s: AiKpiScore | null) => void,
  normalize: (raw: any) => AiKpiScore | null,
) {
  try {
    const res = await analyzeAiKpi(payload);
    if (res.data) setScore(normalize(res.data));
  } catch (e: any) {
    const status = e?.response?.status;
    if (status === 504 || status === 502) {
      // Lambda is still running — poll /ai-kpi-scores until results land
      const startedAt = Date.now();
      const beforeAnalyzedAt = new Date().toISOString();
      const MAX_POLL = 60_000;
      await new Promise<void>((resolve) => {
        const interval = setInterval(async () => {
          if (Date.now() - startedAt > MAX_POLL) { clearInterval(interval); resolve(); return; }
          try {
            const poll = await getAiKpiScore(payload.appId);
            const score = poll.data?.score;
            if (score && score.analyzedAt > beforeAnalyzedAt) {
              setScore(score);
              clearInterval(interval);
              resolve();
            }
          } catch { /* poll is best-effort */ }
        }, 5000);
      });
    }
  }
}

// ── Types ─────────────────────────────────────────────────────
interface ProjectDashboardProps {
  appId: string;
}

type TopLevelView = 'overview' | 'cross-ai-insights' | 'deep-ai-analysis' | 'delta-ai-kpis' | 'epa' | 'security-profile' | 'ai-insights';

const TOOL_TABS = [
  { id: 'sonarqube', label: 'SonarQube', icon: <Bug size={14} /> },
  { id: 'blackduck', label: 'BlackDuck', icon: <Shield size={14} /> },
  { id: 'ghe', label: 'GitLeaks', icon: <Key size={14} /> },
  { id: 'trivy', label: 'Trivy', icon: <Container size={14} /> },
];

/** Normalize a raw /ai-kpi/analyze response (snake_case) into the AiKpiScore interface (camelCase). */
function normalizeAnalyzeResponse(raw: any): AiKpiScore | null {
  if (!raw) return null;
  return {
    appId: raw.project_id || raw.appId || '',
    analyzedAt: raw.analyzedAt || raw.metadata?.analysis_timestamp || null,
    previousAnalyzedAt: raw.previousAnalyzedAt ?? null,
    projectScore: raw.project_score ?? raw.projectScore ?? null,
    projectRag: raw.project_rag ?? raw.projectRag ?? null,
    projectCriticality: raw.project_criticality ?? raw.projectCriticality ?? 'MEDIUM',
    toolCoverage: raw.tool_coverage ?? raw.toolCoverage ?? null,
    repos: (raw.repos || []).map((r: any) => ({
      repoSlug: r.repo_name ?? r.repoSlug ?? '',
      criticality: r.criticality ?? 'MEDIUM',
      repoScore: r.repo_score ?? r.repoScore ?? null,
      repoRag: r.repo_rag ?? r.repoRag ?? null,
      toolScores: r.tool_scores ?? r.toolScores ?? {},
      connectedTools: r.connected_tools ?? r.connectedTools ?? [],
      kpiScores: r.kpi_scores ?? r.kpiScores ?? {},
      topFixes: (r.top_fixes ?? r.topFixes ?? []).map((f: any) => ({
        kpi: f.kpi, tool: f.tool, action: f.action, location: f.location,
        effort: f.effort, impact_points: f.impact_points, priority: f.priority,
      })),
      ragTransition: r.rag_transition ?? r.ragTransition ?? null,
    })),
    aiInsights: raw.ai_insights ?? raw.aiInsights ?? (raw.aiInsightsError
      ? { executive_summary: '', insights: [], error: raw.aiInsightsError }
      : null),
  };
}

// ── Main Component ────────────────────────────────────────────
export default function ProjectDashboard({ appId }: ProjectDashboardProps) {
  const theme = useTheme();
  const { projectName: ctxProjectName, dataSources, projectConfig } = useProjectName();
  const { runScan } = useScan();
  const appName = ctxProjectName || appId || '';

  // ── State ─────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sentinel, setSentinel] = useState<AppInsight | null>(null);
  const [aiKpiScore, setAiKpiScore] = useState<AiKpiScore | null>(null);
  const [dataTimestamp, setDataTimestamp] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Navigation
  const [topView, setTopView] = useState<TopLevelView>('overview');
  const [activeToolTab, setActiveToolTab] = useState(0);
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  // Nav section collapse state — both expanded by default
  const [projectNavOpen, setProjectNavOpen] = useState(true);
  const [repoNavOpen, setRepoNavOpen] = useState(true);
  // Sub-tab inside "AI Insights" view (0 = Deep AI Analysis, 1 = AI KPI Analytics)
  const [aiInsightsTab, setAiInsightsTab] = useState(0);
  // Repository Health detail view — which repo slug is expanded (null = show card list)
  const [repoDetailSlug, setRepoDetailSlug] = useState<string | null>(null);
  const [repoDetailTab, setRepoDetailTab] = useState(0);
  const [repoSortBy, setRepoSortBy] = useState<'health' | 'name'>('health');
  const [repoSearch, setRepoSearch] = useState('');

  // Per-repo Deep AI Analysis results (securityScore etc.) — lifted up from DeepAiAnalysis
  // so both the tab-strip score chip and the tab content itself share the same data/cache.
  const [deepAiRepoResults, setDeepAiRepoResults] = useState<Record<string, RepoSecurityResult>>({});
  const [deepAiRepoLoading, setDeepAiRepoLoading] = useState<Record<string, boolean>>({});
  const fetchedDeepAiReposRef = useRef<Set<string>>(new Set());

  // Pipeline status state
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  const pipelinePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Scan state — per category so Deep AI / EPA / Security Profile can run in parallel
  const [rescanning, setRescanning] = useState(false);
  const [extractionStatuses, setExtractionStatuses] = useState<ExtractionStatuses>({
    'deep-ai': null,
    epa: null,
    'security-profile': null,
  });
  const previousStatusesRef = useRef<ExtractionStatuses>(extractionStatuses);
  const statusPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const justTriggeredRescanRef = useRef(false);
  const [confirmRescanOpen, setConfirmRescanOpen] = useState(false);
  const [pendingScanType, setPendingScanType] = useState<'delta' | 'complete'>('complete');
  const [repoSelectionDialogOpen, setRepoSelectionDialogOpen] = useState(false);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);

  // Cross AI Insights refetch state
  const [crossAiRefreshing, setCrossAiRefreshing] = useState(false);

  // Delta AI KPIs on-demand scan state
  const [aiScanRunning, setAiScanRunning] = useState(false);
  const [aiScanProgress, setAiScanProgress] = useState<ScanProgress | null>(null);
  const aiScanPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // EPA on-demand scan state
  const [epaError, setEpaError] = useState<string | null>(null);
  const [epaEngineDialogOpen, setEpaEngineDialogOpen] = useState(false);

  // Security Profile on-demand scan state
  const [spError, setSpError] = useState<string | null>(null);
  const [spEngineDialogOpen, setSpEngineDialogOpen] = useState(false);

  // Lazy-load flags for all three sentinel sections
  const [findingsLoaded, setFindingsLoaded] = useState(false);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [epaLoaded, setEpaLoaded] = useState(false);
  const [epaLoading, setEpaLoading] = useState(false);
  const [spLoaded, setSpLoaded] = useState(false);
  const [spLoading, setSpLoading] = useState(false);

  // Management access for the project title card
  const [mgmtEntries, setMgmtEntries] = useState<{ email: string; managementLevel: string }[]>([]);

  // Derive default AI engine from project config
  const defaultEngine = (projectConfig?.ai_engine_config?.sentinel?.engine ?? 'claude') as AiEngine;

  // EPA findings filter
  const [epaFilter, setEpaFilter] = useState<'ALL' | 'FAIL' | 'PARTIAL' | 'PASS'>('ALL');
  const [epaExpandedPanel, setEpaExpandedPanel] = useState<string | false>(false);
  const handleEpaAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setEpaExpandedPanel(isExpanded ? panel : false);
  };

  // EPA — Assign Task dialog state
  const [activeEpaTaskFinding, setActiveEpaTaskFinding] = useState<EpaFinding | null>(null);
  const [epaTaskSnackbar, setEpaTaskSnackbar] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });
  const handleEpaAssignTask = (findingId: string, epaFindings: EpaFinding[]) => {
    const finding = epaFindings.find((f) => f.id === findingId);
    if (finding) setActiveEpaTaskFinding(finding);
  };
  const handleEpaTaskSubmit = async (data: {
    findingId: string;
    appId: string;
    task: string;
    taskType: 'fix' | 'reanalyse' | 'mark-resolved';
    resolvedJustification?: string;
  }) => {
    try {
      await sentinelNetwork.post('/task-agent', data);
      setEpaTaskSnackbar({
        open: true,
        severity: 'success',
        message: data.taskType === 'mark-resolved' ? 'Finding marked as resolved.' : 'Task submitted successfully.',
      });
    } catch (err: any) {
      setEpaTaskSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.message || err?.message || 'Failed to submit task.',
      });
      throw err;
    }
  };

  const THREE_MONTHS_MS = 90 * 24 * 60 * 60 * 1000;

  // ── Data Fetching ─────────────────────────────────────────
  const fetchAll = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setError(null);
    try {
      const aiKpiRes = await sentinelNetwork.get('/ai-kpi-scores', { params: { appId } }).catch(() => null);

      if (aiKpiRes?.data) {
        const score = normalizeAnalyzeResponse(aiKpiRes.data?.score);
        setAiKpiScore(score);
        if (score?.analyzedAt) setDataTimestamp(score.analyzedAt);
      }

      // Reset lazy-load flags so sections re-fetch on next tab visit
      setFindingsLoaded(false);
      setEpaLoaded(false);
      setSpLoaded(false);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setSentinel(null);
      } else {
        setError(err.message || 'Failed to load project data');
      }
    } finally {
      setLoading(false);
    }
  }, [appId]);

  const lastFetchRef = useRef(0);
  const fetchAllRef = useRef(fetchAll);
  fetchAllRef.current = fetchAll;

  // Keep a stable ref to fetchFindings so polling callbacks (closures) always call the latest version
  const fetchFindingsRef = useRef<() => Promise<void>>(() => Promise.resolve());


  // ── Initial load ────────────────────────────────────────────
  useEffect(() => {
    fetchAllRef.current();
    lastFetchRef.current = Date.now();
    checkExtractionStatus();

    // Re-fetch when tab becomes visible again, but only if data is stale (>5min)
    const handleVisibility = () => {
      if (!document.hidden && Date.now() - lastFetchRef.current > 300_000) {
        fetchAllRef.current(true);
        lastFetchRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (statusPollRef.current) clearInterval(statusPollRef.current);
      if (pipelinePollRef.current) clearInterval(pipelinePollRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [appId]);

  // ── On mount: auto-trigger pipeline (fast ~15s) or resume polling if already running ──
  const pipelineTriggeredRef = useRef(false);
  useEffect(() => {
    if (pipelineTriggeredRef.current) return;
    pipelineTriggeredRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const currentStatus = await fetchPipelineStatus(appId);
        // Always persist the pipeline record so triggeredAt is available for "LAST SCAN"
        if (!cancelled) setPipelineStatus(currentStatus);
        if (currentStatus.status === 'running') {
          // Already running — resume polling
          if (!cancelled) setIsRefreshing(true);
          const finalStatus = await pollPipelineStatus(appId, (status) => {
            if (!cancelled) setPipelineStatus(status);
          }, 4000, 5 * 60 * 1000);
          if (!cancelled) {
            setPipelineStatus(finalStatus.status === 'running' ? { ...finalStatus, status: 'failed' } : finalStatus);
            await fetchAllRef.current(true);
          }
        } else {
          // Not running — always trigger a fresh scan on every dashboard load.
          if (!cancelled) setIsRefreshing(true);
          try {
            const triggerRes = await triggerScanPipeline(appId, 'auto');
            if (!cancelled) {
              setPipelineStatus({
                status: 'running',
                executionId: triggerRes.executionId,
                triggeredAt: triggerRes.triggeredAt,
                triggeredBy: 'auto',
                tools: {},
                scoring: { status: 'pending' },
              });
            }
            const finalStatus = await pollPipelineStatus(appId, (status) => {
              if (!cancelled) setPipelineStatus(status);
            }, 4000, 5 * 60 * 1000);
            if (!cancelled) {
              setPipelineStatus(finalStatus.status === 'running' ? { ...finalStatus, status: 'failed' } : finalStatus);
              await fetchAllRef.current(true);
            }
          } catch (triggerErr) {
            console.warn('[Pipeline] Auto-trigger failed:', triggerErr);
          }
        }
      } catch (err) {
        console.warn('[Pipeline] Status check failed:', err);
      } finally {
        if (!cancelled) setIsRefreshing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [appId]);

  // ── Auto-trigger pipeline when new repos are detected without score data ──
  // This fires once after the initial load completes if any configured git repo
  // is missing from aiKpiScore.repos (e.g. a repo was added since the last scan).
  const newRepoScanRef = useRef(false);
  useEffect(() => {
    if (newRepoScanRef.current) return;               // already triggered
    if (loading) return;                               // wait until initial fetch finishes
    if (repos.length === 0) return;                    // no repos configured
    if (pipelineStatus?.status === 'running') return;  // pipeline already in progress

    const gitSlugs = dataSources
      .filter((ds: any) => ds.platform === 'Git' && ds.url?.trim())
      .map((ds: any) => ds.url.split('/').pop()?.replace('.git', '') || '');
    const scoredSlugs = new Set((aiKpiScore?.repos ?? []).map((r) => r.repoSlug));
    const unscoredSlugs = gitSlugs.filter((slug) => slug && !scoredSlugs.has(slug));
    if (unscoredSlugs.length === 0) return;            // all repos already have scores

    newRepoScanRef.current = true;
    console.log('[Pipeline] New unscored repos detected — auto-triggering pipeline:', unscoredSlugs);
    triggerScanPipeline(appId, 'auto-new-repo').catch((e) =>
      console.warn('[Pipeline] New-repo auto-trigger failed:', e),
    );
  }, [loading, dataSources, aiKpiScore, pipelineStatus?.status, appId]);

  // ── Manual refetch handler ──────────────────────────────────
  const handleRefetch = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const triggerRes = await triggerScanPipeline(appId, 'manual');
      setPipelineStatus({
        status: 'running',
        executionId: triggerRes.executionId,
        triggeredAt: triggerRes.triggeredAt,
        triggeredBy: 'manual',
        tools: {},
        scoring: { status: 'pending' },
      });
      const finalStatus = await pollPipelineStatus(appId, (status) => {
        setPipelineStatus(status);
      }, 4000, 5 * 60 * 1000);
      if (finalStatus.status === 'running') {
        setPipelineStatus({ ...finalStatus, status: 'failed' });
      } else {
        setPipelineStatus(finalStatus);
      }
      await fetchAllRef.current(true);
    } catch (err) {
      console.error('[Pipeline] Manual refetch failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [appId, isRefreshing]);

  // Sync selectedRepo with repoDetailSlug so ToolTabContent always has the right repo
  useEffect(() => {
    if (repoDetailSlug) setSelectedRepo(repoDetailSlug);
  }, [repoDetailSlug]);

  // Deep AI Analysis: per-repo securityScore results, fetched as soon as a repo row is
  // expanded (not only when the "Deep AI Analysis" tab is opened) so the tab-strip score
  // chip has data available up front, same as the other tool tabs.
  const sentinelExtractedAt = sentinel?.extractedAt;
  useEffect(() => {
    // New scan landed — clear the cache so stale per-repo results aren't shown.
    if (!sentinelExtractedAt) return;
    fetchedDeepAiReposRef.current = new Set();
    setDeepAiRepoResults({});
  }, [sentinelExtractedAt]);

  useEffect(() => {
    if (!selectedRepo) return;
    if (fetchedDeepAiReposRef.current.has(selectedRepo)) return;
    fetchedDeepAiReposRef.current.add(selectedRepo);
    setDeepAiRepoLoading((prev) => ({ ...prev, [selectedRepo]: true }));
    sentinelNetwork
      .get('/sentinel/findings', { params: { appId, repo: selectedRepo } })
      .then((res) => {
        if (res.data) setDeepAiRepoResults((prev) => ({ ...prev, [selectedRepo]: res.data }));
      })
      .catch(() => {})
      .finally(() => setDeepAiRepoLoading((prev) => ({ ...prev, [selectedRepo]: false })));
  }, [selectedRepo, appId, sentinelExtractedAt]);

  // ── Lazy-load Findings / EPA / Security Profile on tab switch ─────────
  const fetchFindings = useCallback(async () => {
    if (findingsLoaded || findingsLoading) return;
    setFindingsLoading(true);
    try {
      const res = await sentinelNetwork.get('/sentinel/findings', { params: { appId } });
      if (res.data) {
        setSentinel(prev => prev ? { ...prev, ...res.data } : res.data);
      }
      setFindingsLoaded(true);
    } catch (err: any) {
      if (err?.response?.status !== 404) console.warn('[Findings] lazy fetch failed:', err?.message);
      setFindingsLoaded(true);
    } finally {
      setFindingsLoading(false);
    }
  }, [appId, findingsLoaded, findingsLoading]);
  fetchFindingsRef.current = fetchFindings;

  const fetchEpa = useCallback(async () => {
    if (epaLoaded || epaLoading) return;
    setEpaLoading(true);
    try {
      const res = await sentinelNetwork.get('/sentinel/epa', { params: { appId } });
      if (res.data) {
        setSentinel(prev => prev ? { ...prev, ...res.data } : res.data);
      }
      setEpaLoaded(true);
    } catch (err: any) {
      if (err?.response?.status !== 404) console.warn('[EPA] lazy fetch failed:', err?.message);
      setEpaLoaded(true); // mark loaded even on 404 to avoid re-fetching
    } finally {
      setEpaLoading(false);
    }
  }, [appId, epaLoaded, epaLoading]);

  const fetchSecurityProfile = useCallback(async () => {
    if (spLoaded || spLoading) return;
    setSpLoading(true);
    try {
      const res = await sentinelNetwork.get('/sentinel/security-profile', { params: { appId } });
      if (res.data) {
        setSentinel(prev => prev ? { ...prev, ...res.data } : res.data);
      }
      setSpLoaded(true);
    } catch (err: any) {
      if (err?.response?.status !== 404) console.warn('[SecurityProfile] lazy fetch failed:', err?.message);
      setSpLoaded(true);
    } finally {
      setSpLoading(false);
    }
  }, [appId, spLoaded, spLoading]);

  useEffect(() => {
    // Also trigger when repoDetailTab === 6 (Deep AI Analysis within repo card)
    if ((topView === 'deep-ai-analysis' || (topView === 'overview' && repoDetailTab === 6)) && !findingsLoaded) fetchFindings();
    if (topView === 'epa' && !epaLoaded) fetchEpa();
    if (topView === 'security-profile' && !spLoaded) fetchSecurityProfile();
  }, [topView, repoDetailTab, findingsLoaded, epaLoaded, spLoaded, fetchFindings, fetchEpa, fetchSecurityProfile]);

  // ── Management access for project title card ──
  useEffect(() => {
    if (!appId) return;
    fetchManagementAccessEntries(appId)
      .then(setMgmtEntries)
      .catch(() => {/* non-critical */ });
  }, [appId]);

  // ── Extraction Polling ────────────────────────────────────
  const checkExtractionStatus = async () => {
    try {
      const statuses = await getExtractionStatuses(appId);
      setExtractionStatuses(statuses);
      previousStatusesRef.current = statuses;
      if (Object.values(statuses).some(s => s?.extractionStatus === 'running')) {
        startStatusPolling();
      }
    } catch { /* ignore */ }
  };

  const startStatusPolling = () => {
    if (statusPollRef.current) clearInterval(statusPollRef.current);
    statusPollRef.current = setInterval(async () => {
      try {
        const statuses = await getExtractionStatuses(appId);
        setExtractionStatuses(statuses);

        const anyRunning = Object.values(statuses).some(s => s?.extractionStatus === 'running');
        if (!anyRunning) {
          if (justTriggeredRescanRef.current) return;
          if (statusPollRef.current) clearInterval(statusPollRef.current);
          statusPollRef.current = null;

          // When Deep AI scan completes, trigger pipeline to refresh scores
          const deepAiWasRunning = previousStatusesRef.current['deep-ai']?.extractionStatus === 'running';
          const epaWasRunning = previousStatusesRef.current['epa']?.extractionStatus === 'running';
          const spWasRunning = previousStatusesRef.current['security-profile']?.extractionStatus === 'running';
          if (deepAiWasRunning) {
            // After pipeline updates KPI scores, refresh the full data AND immediately
            // re-fetch findings so the Deep AI Analysis report renders without needing
            // a manual page reload (works for both topView===deep-ai-analysis and
            // topView===overview with repoDetailTab===5).
            triggerScanPipeline(appId, 'post-deep-ai').catch(() => {}).finally(async () => {
              await fetchAllRef.current(true);
              // fetchAll resets findingsLoaded=false; explicitly fetch findings now
              // so the sentinel prop reaches DeepAiAnalysis immediately rather than
              // waiting for the user to switch tabs.
              fetchFindingsRef.current();
            });
          } else if (epaWasRunning) {
            // Re-fetch only EPA section after EPA scan completes
            setEpaLoaded(false);
            if (topView === 'epa') fetchEpa();
          } else if (spWasRunning) {
            // Re-fetch only Security Profile section after SP scan completes
            setSpLoaded(false);
            if (topView === 'security-profile') fetchSecurityProfile();
          } else {
            fetchAllRef.current(true);
          }
        } else {
          justTriggeredRescanRef.current = false;
        }
        previousStatusesRef.current = statuses;
      } catch { /* ignore */ }
    }, 10000);
  };


  const handleRescan = async (scanType: 'delta' | 'complete' = 'delta', selectedRepos?: string[], agentMode?: string) => {
    try {
      setRescanning(true);
      await sentinelNetwork.post('/extract', { appId, scanType, scanCategory: 'deep-ai', repos: selectedRepos, ...(agentMode ? { agentMode } : {}) });
      justTriggeredRescanRef.current = true;
      const initialRepoProgress: Record<string, any> = {};
      if (selectedRepos) {
        for (const slug of selectedRepos) initialRepoProgress[slug] = { status: 'queued' };
      }
      setExtractionStatuses(prev => ({
        ...prev,
        'deep-ai': {
          extractionStatus: 'running',
          extractionStep: 'starting',
          ...(selectedRepos && { repoProgress: initialRepoProgress }),
        },
      }));
      startStatusPolling();

      runScan({ key: `ai:${appId}`, label: `${appName} — AI security scan` }, async (setMessage) => {
        setMessage('Analyzing repositories…');
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, 8000));
          const status = await getExtractionStatus(appId, 'deep-ai');
          if (!status || status.extractionStatus === 'completed') return { status: 'ok', message: 'AI security scan completed' };
          if (status.extractionStatus === 'failed') return { status: 'error', message: status.extractionError || 'AI security scan failed' };
        }
      });
    } catch (err: any) {
      setError(err.message || 'Failed to start scan');
    } finally {
      setRescanning(false);
    }
  };

  const handleOnDemandScan = async (category: 'epa' | 'security-profile', agentMode?: string) => {
    const setErr = category === 'epa' ? setEpaError : setSpError;
    setErr(null);
    try {
      await triggerOnDemandScan(appId, category, agentMode);
      justTriggeredRescanRef.current = true;
      setExtractionStatuses(prev => ({ ...prev, [category]: { extractionStatus: 'running', extractionStep: 'starting' } }));
      startStatusPolling();
    } catch (err: any) {
      setErr(err.message || `Failed to start ${category} scan`);
    }
  };

  /** Fetch (or re-fetch) Cross AI Insights by calling analyzeAiKpi with includeAiInsights: true */
  const handleFetchCrossAiInsights = async () => {
    const gitRepos = dataSources
      .filter((ds: any) => ds.platform === 'Git' && ds.url?.trim())
      .map((ds: any) => {
        const match = ds.url.match(/\/([^/]+?)(?:\.git)?$/);
        return { name: match ? match[1] : ds.url, url: ds.url, branch: ds.branch || 'main' };
      });
    if (gitRepos.length === 0) return;
    setCrossAiRefreshing(true);
    try {
      await analyzeWithFallbackPoll({
        appId,
        repos: gitRepos.map(r => ({ repoSlug: r.name, branch: r.branch })),
        includeAiInsights: true,
      }, setAiKpiScore, normalizeAnalyzeResponse);
    } catch (err: any) {
      console.error('[CrossAiInsights] refetch failed:', err?.message || err);
    } finally {
      setCrossAiRefreshing(false);
    }
  };


  /** Trigger an on-demand AI KPI scan (Batch job), poll progress, re-score on completion */
  /** Trigger AI scan. When `repoSlug` is provided, scan only that repo (per-repo tab);
   *  when omitted, scan all configured git repositories (project-level button). */
  const handleTriggerAiScan = async (repoSlug?: string) => {
    const allGitRepos = dataSources
      .filter((ds: any) => ds.platform === 'Git' && ds.url?.trim())
      .map((ds: any) => {
        const match = ds.url.match(/\/([^/]+?)(?:\.git)?$/);
        return { repoSlug: match ? match[1] : ds.url, url: ds.url, branch: ds.branch || 'main' };
      });
    const gitRepos = repoSlug
      ? allGitRepos.filter((r) => r.repoSlug === repoSlug)
      : allGitRepos;
    if (gitRepos.length === 0) return;

    try {
      setAiScanRunning(true);
      setAiScanProgress({ status: 'running', passes_completed: 0, passes_total: 4, current_pass: null });
      await triggerAiScanApi(appId, gitRepos);

      // Poll progress every 8 seconds
      if (aiScanPollRef.current) clearInterval(aiScanPollRef.current);
      aiScanPollRef.current = setInterval(async () => {
        try {
          const res = await getAiScanStatusApi(appId);
          const data = res.data;
          if (data.progress) setAiScanProgress(data.progress);

          if (data.status === 'completed' || data.status === 'failed') {
            if (aiScanPollRef.current) clearInterval(aiScanPollRef.current);
            aiScanPollRef.current = null;
            // Keep aiScanRunning=true while scoring + fetch run so the UI stays
            // in the "scanning" state — scores will be fresh by the time we clear it.

            if (data.status === 'completed') {
              // Invoke the scoring lambda to compute ai_agent KPI scores from the
              // raw AI scan data just written by the Batch job. Without this call,
              // SENTINEL_AI_KPI_SCORES_TABLE has no ai_agent scores → hasAiData=false.
              try {
                const scoringRepos = allGitRepos.map((r) => ({
                  repoSlug: r.repoSlug,
                  branch: r.branch,
                }));
                await analyzeWithFallbackPoll(
                  { appId, repos: scoringRepos },
                  setAiKpiScore,
                  normalizeAnalyzeResponse,
                );
              } catch { /* non-critical — fetchAll below will still refresh */ }
              // Re-fetch to pick up anything analyzeWithFallbackPoll may have missed
              await fetchAll(true);
              // Trigger scoring pipeline to update health scores with new AI data
              try { await triggerScanPipeline(appId); } catch { /* best effort */ }
            }

            // Clear scan state only after fresh data is loaded
            setAiScanRunning(false);
            setAiScanProgress(null);
          }
        } catch {
          // ignore transient poll errors
        }
      }, 8000);

      runScan({ key: `ai-kpi:${appId}`, label: `${appName} — Delta AI KPI scan` }, async (setMessage) => {
        setMessage('Running 4-pass AI analysis…');
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, 8000));
          try {
            const res = await getAiScanStatusApi(appId);
            const data = res.data;
            if (data.status === 'completed') return { status: 'ok', message: 'Delta AI KPI scan completed' };
            if (data.status === 'failed') return { status: 'error', message: 'Delta AI KPI scan failed' };
            if (data.progress?.current_pass) setMessage(`Pass ${data.progress.passes_completed + 1}/4: ${data.progress.current_pass}`);
          } catch { /* ignore */ }
        }
      });
    } catch (err: any) {
      setError(err.message || 'Failed to start AI scan');
      setAiScanRunning(false);
      setAiScanProgress(null);
    }
  };

  // Cleanup AI scan poll on unmount
  useEffect(() => {
    return () => {
      if (aiScanPollRef.current) clearInterval(aiScanPollRef.current);
    };
  }, []);

  const handleOpenRepoSelectionDialog = (scanType: 'delta' | 'complete' = 'complete') => {
    setPendingScanType(scanType);
    if (sentinel?.extractedAt && Date.now() - new Date(sentinel.extractedAt).getTime() < THREE_MONTHS_MS) {
      setConfirmRescanOpen(true);
      return;
    }
    setRepoSelectionDialogOpen(true);
  };

  // ── Derived Data ──────────────────────────────────────────
  const repos = useMemo(() => {
    return dataSources
      .filter((ds: any) => ds.platform === 'Git' && ds.url?.trim())
      .map((ds: any) => {
        const slug = ds.url?.split('/').pop()?.replace('.git', '') || '';
        return { slug, url: ds.url, branch: ds.branch || 'main', category: ds.category || '' };
      });
  }, [dataSources]);

  const selectedRepoScore: AiKpiRepoScore | undefined = useMemo(() => {
    if (!aiKpiScore?.repos || !selectedRepo) return undefined;
    return aiKpiScore.repos.find((r) => r.repoSlug === selectedRepo);
  }, [aiKpiScore, selectedRepo]);

  // Auto-select first repo — prefer first repo that has any non-zero security tool score
  useEffect(() => {
    if (!selectedRepo && repos.length > 0) {
      const firstWithScore = aiKpiScore?.repos?.find((r) => {
        const ts = r.toolScores ?? {};
        return Object.values(ts).some((v) => typeof v === 'number' && (v as number) > 0);
      });
      setSelectedRepo(firstWithScore?.repoSlug ?? repos[0].slug);
    }
  }, [repos, selectedRepo, aiKpiScore]);

  const toolCoverage = aiKpiScore?.toolCoverage;
  const projectScore = aiKpiScore?.projectScore;
  // "LAST SCAN" = when the current pipeline was triggered (data collection started).
  // "As of"    = aiKpiScore.previousAnalyzedAt (when the PREVIOUS score was computed).
  //   • previousAnalyzedAt is always hours/days before lastScanDate — they are never the same.
  //   • On the very first pipeline run previousAnalyzedAt is null, so the "As of" chip is hidden.
  const lastScanDate = pipelineStatus?.triggeredAt || aiKpiScore?.analyzedAt || sentinel?.extractedAt;
  const asOfDate = aiKpiScore?.previousAnalyzedAt ?? null;

  // Per-section scoped extraction flags
  const isDeepAiExtracting = extractionStatuses['deep-ai']?.extractionStatus === 'running';
  const isEpaExtracting    = extractionStatuses['epa']?.extractionStatus === 'running';
  const isSpExtracting     = extractionStatuses['security-profile']?.extractionStatus === 'running';
  const isBaseExtracting   = isDeepAiExtracting || isEpaExtracting || isSpExtracting;

  // Collect all top fixes across repos for prioritized issues
  const allTopFixes: PrioritizedIssue[] = useMemo(() => {
    if (!aiKpiScore?.repos) return [];
    return aiKpiScore.repos.flatMap((r) =>
      (r.topFixes || []).map((f) => ({
        ...f,
        repository: r.repoSlug,
        severity: f.priority,
      }))
    );
  }, [aiKpiScore]);

  const aiInsights: AiInsight[] = useMemo(() => {
    return (aiKpiScore?.aiInsights?.insights || []) as AiInsight[];
  }, [aiKpiScore]);

  const executiveSummary = aiKpiScore?.aiInsights?.executive_summary || '';
  const aiInsightsError = aiKpiScore?.aiInsights?.error || '';

  // ── Loading ───────────────────────────────────────────────
  if (loading) return <NerdSpinner fullPage />;

  // ── Render ────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 }, pb: 6 }}>

          {/* ═══════════════════════════════════════════════════
              1. BREADCRUMB + ACTIONS
              ═══════════════════════════════════════════════════ */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
            <Breadcrumbs aria-label="breadcrumb">
              <StyledBreadcrumb label="Projects" icon={<Home size={14} />} onClick={() => window.location.href = '/projects'} sx={{ cursor: 'pointer' }} />
              <StyledBreadcrumb label={appName} icon={<Shield size={14} />} />
            </Breadcrumbs>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

          {/* ═══════════════════════════════════════════════════
              2. PROJECT HEADER (3-column layout)
              ═══════════════════════════════════════════════════ */}
          <SectionCard>
            <Grid container spacing={0} sx={{ minHeight: { xs: 'auto', md: 120 } }}>
              {/* ── Column 1: Project Info ─────────────────────── */}
              <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' }, pr: { md: 2 }, pb: { xs: 1.5, md: 0 } }}>
                <Stack spacing={1}>
                  <Stack spacing={0.25}>
                    <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: 18, md: 22 }, lineHeight: 1.2 }}>
                      {appName}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {(projectConfig?.leanIXId || projectConfig?.leanIxId) && (
                      <Chip label={projectConfig.leanIXId || projectConfig.leanIxId} size="small" variant="outlined" sx={{ height: 22, fontSize: 11, fontWeight: 600, borderRadius: 1 }} />
                    )}
                    <ProjectStatusBadge lastScanDate={lastScanDate} />
                  </Stack>


                  {/* ── Management access contacts ── */}
                  {mgmtEntries.length > 0 && (
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                        Management:
                      </Typography>
                      {mgmtEntries.map((e, i) => (
                        <Chip
                          key={i}
                          label={`${e.managementLevel}: ${e.email}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.6rem', fontWeight: 500, borderRadius: 1, color: 'text.secondary', maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                        />
                      ))}
                    </Stack>
                  )}

                </Stack>
              </Grid>

              {/* ── Column 2: Health Score ─────────────────────── */}
              <Grid item xs={12} md={6} sx={{ px: { md: 2 }, py: { xs: 1.5, md: 0 } }}>
                <Stack spacing={1}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>
                    Health Score
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <ScoreWidget
                      score={projectScore ?? null}
                      size={80}
                      label=""
                      showRag
                      rag={aiKpiScore?.projectRag}
                    />
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      {(() => {
                        const connectedArr = (toolCoverage?.connected || []).sort();
                        const allTools = toolCoverage ? (toolCoverage.connected?.length || 0) + (toolCoverage.not_connected?.length || 0) || 5 : 5;
                        return (
                          <>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                              Score calculated from {connectedArr.length} of {allTools} security checks
                            </Typography>
                            {connectedArr.length > 0 && (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {connectedArr.map((t: string) => {
                                  const def = findToolDef(t);
                                  const label = def?.label || t;
                                  return (
                                    <Chip key={t} label={label} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 600, bgcolor: alpha('#22c55e', 0.1), color: '#22c55e', borderRadius: 1 }} />
                                  );
                                })}
                              </Stack>
                            )}
                          </>
                        );
                      })()}
                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={projectScore ?? 0}
                          sx={{
                            height: 8, borderRadius: 4,
                            bgcolor: alpha(theme.palette.text.primary, 0.06),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${
                                (projectScore ?? 0) >= 80 ? '#22c55e' : (projectScore ?? 0) >= 60 ? '#f59e0b' : '#ef4444'
                              } 0%, ${
                                (projectScore ?? 0) >= 80 ? '#16a34a' : (projectScore ?? 0) >= 60 ? '#d97706' : '#dc2626'
                              } 100%)`,
                            },
                          }}
                        />
                        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            {projectScore != null ? Math.round(projectScore) : '–'} <Typography component="span" sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 400 }}>/ 100</Typography>
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </Stack>
                </Stack>
              </Grid>

            </Grid>
          </SectionCard>

          {/* ═══════════════════════════════════════════════════
              3. TOP-LEVEL NAVIGATION — horizontal tabs
              ═══════════════════════════════════════════════════ */}
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mt: 3, mb: 0 }}>
            <Tabs
              value={['overview','cross-ai-insights','epa','security-profile'].includes(topView) ? topView : 'overview'}
              onChange={(_, v) => { setTopView(v as TopLevelView); setRepoDetailSlug(null); setRepoDetailTab(0); }}
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 44, py: 0 },
                '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
              }}
            >
              <Tab value="overview" icon={<LayoutDashboard size={14} />} iconPosition="start" label="Repository Health" />
              <Tab value="cross-ai-insights" icon={<Sparkles size={14} />} iconPosition="start" label="Risk AI Analysis" />
              <Tab value="epa" icon={<Eye size={14} />} iconPosition="start" label="EPA Readiness" />
              <Tab value="security-profile" icon={<FileCheck2 size={14} />} iconPosition="start" label="Security Profile Readiness" />
            </Tabs>
          </Box>

          {/* ═══════════════════════════════════════════════════
              DELTA AI KPIs VIEW
              ═══════════════════════════════════════════════════ */}
          {topView === 'delta-ai-kpis' && (() => {
            const hasAiData = !!(aiKpiScore?.repos?.some(r => r.toolScores?.ai_agent != null));
            const lastAiScanDate = aiKpiScore?.analyzedAt;

            return (
              <Stack spacing={3}>
                {/* Action bar — matches Deep AI Analysis pattern */}
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 1, px: 2, py: 1.25,
                    borderRadius: 2, border: '1px solid', borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {aiScanRunning && aiScanProgress && (
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mr: 1 }}>
                        <Box sx={{
                          width: 6, height: 6, borderRadius: '50%', bgcolor: '#0B6BCB',
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                        }} />
                        <Typography sx={{ fontSize: 11, color: '#0B6BCB', fontWeight: 500 }}>
                          {aiScanProgress.current_pass
                            ? `Pass ${aiScanProgress.passes_completed + 1}/4: ${aiScanProgress.current_pass}`
                            : `AI scan: ${aiScanProgress.passes_completed}/4 passes completed`}
                        </Typography>
                      </Stack>
                    )}
                    {!aiScanRunning && lastAiScanDate && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.disabled', mr: 1 }}>
                        <Clock size={13} />
                        <Typography sx={{ fontSize: 11 }}>
                          {new Date(lastAiScanDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Button
                    variant="contained"
                    size="small"
                    startIcon={aiScanRunning ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
                    onClick={() => handleTriggerAiScan()}
                    disabled={aiScanRunning}
                    disableElevation
                    sx={{
                      textTransform: 'none', fontWeight: 700,
                      background: '#0B6BCB',
                      '&:hover': { background: '#185ABC' },
                      '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 },
                    }}
                  >
                    {aiScanRunning ? 'Scanning...' : hasAiData ? 'Re-scan' : 'Run AI Scan'}
                  </Button>
                </Paper>

                {/* Scanning progress */}
                {aiScanRunning && aiScanProgress && (
                  <SectionCard title="AI KPI Scan" icon={<Gauge size={16} />}>
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Box sx={{
                        width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 3,
                        bgcolor: alpha('#0B6BCB', 0.1),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.7, transform: 'scale(0.95)' } },
                      }}>
                        <Gauge size={28} color="#0B6BCB" />
                      </Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>4-Pass AI Analysis in Progress</Typography>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3 }}>
                        Cloning repos and running LLM-powered security analysis across {repos.length} repositories.
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(aiScanProgress.passes_completed / aiScanProgress.passes_total) * 100}
                        sx={{ height: 6, borderRadius: 3, mx: 'auto', maxWidth: 360, bgcolor: alpha('#0B6BCB', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#0B6BCB', borderRadius: 3 } }}
                      />
                      <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>
                        {aiScanProgress.passes_completed} of {aiScanProgress.passes_total} passes completed
                        {aiScanProgress.current_pass && ` — ${aiScanProgress.current_pass}`}
                      </Typography>
                    </Box>
                  </SectionCard>
                )}

                {/* Empty state */}
                {!aiScanRunning && !hasAiData && (
                  <SectionCard>
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <Box sx={{
                        width: 64, height: 64, borderRadius: 3, mx: 'auto', mb: 3,
                        bgcolor: alpha('#0B6BCB', 0.08),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Gauge size={32} color="#0B6BCB" />
                      </Box>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 1 }}>AI KPI Analytics</Typography>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3, maxWidth: 480, mx: 'auto' }}>
                        Run a 4-pass AI-powered analysis to generate advanced KPIs including SBOM coverage, dependency confusion risk,
                        EPSS scores, reachable vulnerability analysis, and SLA compliance metrics.
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<Play size={16} />}
                        onClick={() => handleTriggerAiScan()}
                        disableElevation
                        sx={{
                          borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 4, py: 1.25,
                          background: '#0B6BCB',
                          '&:hover': { background: '#185ABC' },
                          '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 },
                        }}
                      >
                        Run AI Scan
                      </Button>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 2 }}>
                        Without AI scan data, the ai_agent component (25% weight) scores 0 — capping health score at ~75.
                      </Typography>
                    </Box>
                  </SectionCard>
                )}

                {/* KPI data display */}
                {!aiScanRunning && hasAiData && selectedRepoScore && (
                  <DeltaAiKpisTab
                    toolScore={selectedRepoScore.toolScores?.ai_agent ?? null}
                    kpiScores={selectedRepoScore.kpiScores}
                    topFixes={selectedRepoScore.topFixes}
                    loading={false}
                    scanning={false}
                    scanProgress={null}
                  />
                )}
              </Stack>
            );
          })()}

          {/* ═══════════════════════════════════════════════════
              AI INSIGHTS VIEW (Deep AI Analysis + AI KPI Analytics tabs)
              ═══════════════════════════════════════════════════ */}
          {topView === 'ai-insights' && (() => {
            const hasAiData = !!(aiKpiScore?.repos?.some(r => r.toolScores?.ai_agent != null));
            const lastAiScanDate = aiKpiScore?.analyzedAt;
            return (
              <Stack spacing={2}>
                {/* Repo selector — mirrors Repository Insights so users can pick a repo in any sub-tab */}
                {repos.length > 1 && (
                  <RepoRadioBar
                    repos={repos}
                    selectedRepo={selectedRepo}
                    onChange={setSelectedRepo}
                    repoProgress={extractionStatuses['deep-ai']?.repoProgress}
                    isScanning={isDeepAiExtracting}
                  />
                )}
                {/* Tab strip */}
                <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Tabs
                    value={aiInsightsTab}
                    onChange={(_, v) => setAiInsightsTab(v)}
                    sx={{
                      '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 44, py: 0 },
                      '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
                    }}
                  >
                    <Tab label="Deep AI Analysis" icon={<Brain size={14} />} iconPosition="start" />
                    <Tab label="AI KPI Analytics" icon={<Gauge size={14} />} iconPosition="start" />
                  </Tabs>
                </Box>

                {/* Tab 0 — Deep AI Analysis */}
                {aiInsightsTab === 0 && (
                  <DeepAiAnalysis
                    sentinel={sentinel}
                    appId={appId}
                    appName={appName}
                    extractionStatus={extractionStatuses['deep-ai']}
                    isSectionExtracting={isDeepAiExtracting}
                    onRunAnalysis={() => handleOpenRepoSelectionDialog('complete')}
                    isRunning={rescanning}
                    lastScanDate={lastScanDate}
                    onWorkflow={() => setWorkflowDialogOpen(true)}
                    selectedRepo={selectedRepo}
                    onRepoChange={setSelectedRepo}
                    repoProgress={extractionStatuses['deep-ai']?.repoProgress}
                    repos={repos}
                    repoResults={deepAiRepoResults}
                    repoLoading={deepAiRepoLoading}
                  />
                )}

                {/* Tab 1 — AI KPI Analytics */}
                {aiInsightsTab === 1 && (
                  <Stack spacing={3}>
                    {/* Action bar */}
                    <Paper
                      elevation={0}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 1, px: 2, py: 1.25,
                        borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        {aiScanRunning && aiScanProgress && (
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mr: 1 }}>
                            <Box sx={{
                              width: 6, height: 6, borderRadius: '50%', bgcolor: '#0B6BCB',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                            }} />
                            <Typography sx={{ fontSize: 11, color: '#0B6BCB', fontWeight: 500 }}>
                              {aiScanProgress.current_pass
                                ? `Pass ${aiScanProgress.passes_completed + 1}/4: ${aiScanProgress.current_pass}`
                                : `AI scan: ${aiScanProgress.passes_completed}/4 passes completed`}
                            </Typography>
                          </Stack>
                        )}
                        {!aiScanRunning && lastAiScanDate && (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.disabled', mr: 1 }}>
                            <Clock size={13} />
                            <Typography sx={{ fontSize: 11 }}>
                              {new Date(lastAiScanDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={aiScanRunning ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
                        onClick={() => handleTriggerAiScan()}
                        disabled={aiScanRunning}
                        disableElevation
                        sx={{
                          textTransform: 'none', fontWeight: 700,
                          background: '#0B6BCB',
                          '&:hover': { background: '#185ABC' },
                          '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 },
                        }}
                      >
                        {aiScanRunning ? 'Scanning...' : hasAiData ? 'Re-scan' : 'Run AI Scan'}
                      </Button>
                    </Paper>

                    {/* Scanning progress */}
                    {aiScanRunning && aiScanProgress && (
                      <SectionCard title="AI KPI Scan" icon={<Gauge size={16} />}>
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <Box sx={{
                            width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 3,
                            bgcolor: alpha('#0B6BCB', 0.1),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            animation: 'pulse 2s ease-in-out infinite',
                            '@keyframes pulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.7, transform: 'scale(0.95)' } },
                          }}>
                            <Gauge size={28} color="#0B6BCB" />
                          </Box>
                          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>4-Pass AI Analysis in Progress</Typography>
                          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3 }}>
                            Cloning repos and running LLM-powered security analysis across {repos.length} repositories.
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(aiScanProgress.passes_completed / aiScanProgress.passes_total) * 100}
                            sx={{ height: 6, borderRadius: 3, mx: 'auto', maxWidth: 360, bgcolor: alpha('#0B6BCB', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#0B6BCB', borderRadius: 3 } }}
                          />
                          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>
                            {aiScanProgress.passes_completed} of {aiScanProgress.passes_total} passes completed
                            {aiScanProgress.current_pass && ` — ${aiScanProgress.current_pass}`}
                          </Typography>
                        </Box>
                      </SectionCard>
                    )}

                    {/* Empty state */}
                    {!aiScanRunning && !hasAiData && (
                      <SectionCard>
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                          <Box sx={{
                            width: 64, height: 64, borderRadius: 3, mx: 'auto', mb: 3,
                            bgcolor: alpha('#0B6BCB', 0.08),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Gauge size={32} color="#0B6BCB" />
                          </Box>
                          <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 1 }}>AI KPI Analytics</Typography>
                          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3, maxWidth: 480, mx: 'auto' }}>
                            Run a 4-pass AI-powered analysis to generate advanced KPIs including SBOM coverage, dependency confusion risk,
                            EPSS scores, reachable vulnerability analysis, and SLA compliance metrics.
                          </Typography>
                          <Button
                            variant="contained"
                            size="large"
                            startIcon={<Play size={16} />}
                            onClick={() => handleTriggerAiScan()}
                            disableElevation
                            sx={{
                              borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 4, py: 1.25,
                              background: '#0B6BCB',
                              '&:hover': { background: '#185ABC' },
                              '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 },
                            }}
                          >
                            Run AI Scan
                          </Button>
                          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 2 }}>
                            Without AI scan data, the ai_agent component (25% weight) scores 0 — capping health score at ~75.
                          </Typography>
                        </Box>
                      </SectionCard>
                    )}

                    {/* KPI data display */}
                    {!aiScanRunning && hasAiData && selectedRepoScore && (
                      <DeltaAiKpisTab
                        toolScore={selectedRepoScore.toolScores?.ai_agent ?? null}
                        kpiScores={selectedRepoScore.kpiScores}
                        topFixes={selectedRepoScore.topFixes}
                        loading={false}
                        scanning={false}
                        scanProgress={null}
                      />
                    )}
                  </Stack>
                )}
              </Stack>
            );
          })()}

          {/* ═══════════════════════════════════════════════════
              CROSS AI INSIGHTS VIEW
              ═══════════════════════════════════════════════════ */}
          {topView === 'cross-ai-insights' && (
            <CrossAiInsights
              executiveSummary={executiveSummary}
              insights={aiInsights}
              loading={false}
              error={aiInsightsError}
              onRefetch={handleFetchCrossAiInsights}
              refreshing={crossAiRefreshing}
              lastAnalyzedAt={aiKpiScore?.analyzedAt}
              isRefreshing={isRefreshing}
            />
          )}

          {/* ═══════════════════════════════════════════════════
              DEEP AI ANALYSIS VIEW
              ═══════════════════════════════════════════════════ */}
          {topView === 'deep-ai-analysis' && (
            <DeepAiAnalysis
              sentinel={sentinel}
              appId={appId}
              appName={appName}
              extractionStatus={extractionStatuses['deep-ai']}
              isSectionExtracting={isDeepAiExtracting}
              onRunAnalysis={() => handleOpenRepoSelectionDialog('complete')}
              isRunning={rescanning}
              lastScanDate={lastScanDate}
              onWorkflow={() => setWorkflowDialogOpen(true)}
              selectedRepo={selectedRepo}
              onRepoChange={setSelectedRepo}
              repoProgress={extractionStatuses['deep-ai']?.repoProgress}
              repos={repos}
              repoResults={deepAiRepoResults}
              repoLoading={deepAiRepoLoading}
            />
          )}

          {/* ═══════════════════════════════════════════════════
              EPA VIEW
              ═══════════════════════════════════════════════════ */}
          {topView === 'epa' && epaLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {topView === 'epa' && !epaLoading && (() => {
            const isExtracting = isEpaExtracting;
            const epaSummary = sentinel?.epaSummary;
            const epaFindings: EpaFinding[] = epaSummary?.findings || [];
            const filteredFindings = epaFilter === 'ALL' ? epaFindings : epaFindings.filter(f => f.status === epaFilter);

            const handleDownloadEpaReport = () => {
              if (!epaSummary) return;
              try {
                exportEpaReportPdf(epaSummary, appName);
              } catch (err: any) {
                console.error('EPA report download failed:', err);
              }
            };

            return (
              <Stack spacing={3}>
                {/* Action bar */}
                <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {isExtracting && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#0B6BCB', animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
                        <Typography sx={{ fontSize: 11, color: '#0B6BCB', fontWeight: 500 }}>
                          {extractionStatuses['epa']?.extractionStep === 'analyzing' && extractionStatuses['epa']?.totalRepos
                            ? `EPA scan: Scanning repos (${extractionStatuses['epa'].completedRepos || 0}/${extractionStatuses['epa'].totalRepos})`
                            : extractionStatuses['epa']?.extractionStep === 'analyzing' && extractionStatuses['epa']?.extractionProgress
                            ? `EPA scan: Analyzing (${extractionStatuses['epa'].extractionProgress.completed || 0}/${extractionStatuses['epa'].extractionProgress.total || '?'})`
                            : extractionStatuses['epa']?.extractionStep === 'checkout' ? 'EPA scan: Checking out code...'
                            : extractionStatuses['epa']?.extractionStep === 'storing' ? 'EPA scan: Saving results...'
                            : 'Running EPA scan...'}
                        </Typography>
                      </Stack>
                    )}
                    {!isExtracting && epaSummary?.findings && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.disabled' }}>
                        <Clock size={13} />
                        <Typography sx={{ fontSize: 11 }}>{sentinel?.extractedAt ? new Date(sentinel.extractedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</Typography>
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {epaError && <Typography sx={{ fontSize: 11, color: 'error.main' }}>{epaError}</Typography>}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={isExtracting ? <CircularProgress size={14} color="inherit" /> : epaSummary ? <RefreshCw size={14} /> : <Play size={14} />}
                      onClick={() => setEpaEngineDialogOpen(true)}
                      disabled={isExtracting}
                      disableElevation
                      sx={{ textTransform: 'none', fontWeight: 700, background: '#0B6BCB', '&:hover': { background: '#185ABC' }, '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 } }}
                    >
                      {isExtracting ? 'Starting...' : epaSummary ? 'Re-run EPA Scan' : 'Run EPA Scan'}
                    </Button>
                  </Stack>
                </Paper>

                {/* Empty state */}
                {!epaSummary && !isExtracting && (
                  <SectionCard>
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <Box sx={{ width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 3, bgcolor: alpha('#0B6BCB', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Eye size={28} color="#0B6BCB" />
                      </Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>No EPA Scan Yet</Typography>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3, maxWidth: 440, mx: 'auto' }}>
                        Run an on-demand Enterprise Privacy Assessment (EPA) scan to identify privacy and compliance findings across all repositories.
                      </Typography>
                      <Button variant="contained" size="large" startIcon={<Play size={16} />} onClick={() => setEpaEngineDialogOpen(true)} disabled={isExtracting} disableElevation
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 4, py: 1.25, background: '#0B6BCB', '&:hover': { background: '#185ABC' }, '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 } }}>
                        Run EPA Scan
                      </Button>
                    </Box>
                  </SectionCard>
                )}

                {/* Scanning progress */}
                {isExtracting && !epaSummary && (
                  <SectionCard title="EPA Scan" icon={<Eye size={16} />}>
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Box sx={{ width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 3, bgcolor: alpha('#0B6BCB', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 2s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.7, transform: 'scale(0.95)' } } }}>
                        <Eye size={28} color="#0B6BCB" />
                      </Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>EPA Scan in Progress</Typography>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3 }}>AI is analyzing privacy and compliance across all repositories...</Typography>
                      <Stack spacing={1.5} sx={{ maxWidth: 360, mx: 'auto', textAlign: 'left' }}>
                        {([
                          { step: 'checkout', label: 'Checking out repositories', icon: <Layers size={14} /> },
                          { step: 'analyzing', label: 'Scanning & analyzing code', icon: <Brain size={14} /> },
                          { step: 'storing',  label: 'Saving results',            icon: <FileText size={14} /> },
                        ] as const).map(({ step, label, icon }) => {
                          const currentStep = extractionStatuses['epa']?.extractionStep || '';
                          const steps = ['checkout', 'analyzing', 'storing'];
                          const isActive = currentStep === step;
                          const isDone = steps.indexOf(step) < steps.indexOf(currentStep);
                          return (
                            <Stack key={step} direction="row" spacing={1.5} alignItems="center" sx={{ opacity: isDone || isActive ? 1 : 0.3 }}>
                              <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: isDone ? alpha('#22c55e', 0.1) : isActive ? alpha('#0B6BCB', 0.1) : 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDone ? '#22c55e' : isActive ? '#0B6BCB' : 'text.disabled' }}>
                                {isDone ? <CheckCircle2 size={14} /> : isActive ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : icon}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? 'text.primary' : 'text.secondary' }}>{label}</Typography>
                                {isActive && extractionStatuses['epa']?.totalRepos && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography sx={{ fontSize: 10, color: 'text.disabled', mb: 0.5 }}>{extractionStatuses['epa'].completedRepos || 0} of {extractionStatuses['epa'].totalRepos} repos</Typography>
                                    <LinearProgress variant="determinate" value={((extractionStatuses['epa'].completedRepos || 0) / extractionStatuses['epa'].totalRepos) * 100} sx={{ height: 3, borderRadius: 2, bgcolor: alpha('#0B6BCB', 0.1), '& .MuiLinearProgress-bar': { bgcolor: '#0B6BCB' } }} />
                                  </Box>
                                )}
                              </Box>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  </SectionCard>
                )}

                {/* Results */}
                {epaSummary && (
                  <SectionCard
                    title="EPA Findings"
                    icon={<Eye size={16} />}
                    action={
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download size={14} />}
                        onClick={handleDownloadEpaReport}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Download EPA Report
                      </Button>
                    }
                  >
                    <Stack spacing={2}>
                      {/* Summary chips */}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>{epaSummary.totalFindings} findings:</Typography>
                        {[{label: `${epaSummary.failCount} FAIL`, color: '#ef4444'}, {label: `${epaSummary.partialCount} PARTIAL`, color: '#f59e0b'}, {label: `${epaSummary.passCount} PASS`, color: '#22c55e'}].map(s => (
                          <Chip key={s.label} label={s.label} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: alpha(s.color, 0.1), color: s.color, borderRadius: 1 }} />
                        ))}
                        {epaSummary.highCount > 0 && <Chip label={`${epaSummary.highCount} High`} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: alpha('#ef4444', 0.08), color: '#ef4444', borderRadius: 1 }} />}
                      </Stack>

                      {/* Filter bar */}
                      <Stack direction="row" spacing={0.75}>
                        {(['ALL', 'FAIL', 'PARTIAL', 'PASS'] as const).map(f => (
                          <Chip key={f} label={f} size="small" onClick={() => setEpaFilter(f)}
                            sx={{ height: 26, fontSize: 11, fontWeight: 600, cursor: 'pointer', bgcolor: epaFilter === f ? 'primary.main' : 'transparent', color: epaFilter === f ? 'primary.contrastText' : 'text.secondary', border: '1px solid', borderColor: epaFilter === f ? 'primary.main' : 'divider', '&:hover': { bgcolor: epaFilter === f ? 'primary.dark' : alpha(theme.palette.primary.main, 0.06) } }} />
                        ))}
                      </Stack>

                      {/* Findings list */}
                      {filteredFindings.length === 0 && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2, textAlign: 'center' }}>No findings match the selected filter.</Typography>
                      )}
                      {filteredFindings.map((finding, idx) => (
                        <EpaFindingAccordion
                          key={finding.id || idx}
                          finding={finding}
                          index={idx}
                          expandedPanel={epaExpandedPanel}
                          onAccordionChange={handleEpaAccordionChange}
                          onAssignTask={(findingId) => handleEpaAssignTask(findingId, epaFindings)}
                        />
                      ))}
                    </Stack>
                  </SectionCard>
                )}

                {/* Assign Task Dialog */}
                {activeEpaTaskFinding && (
                  <SecurityTaskDialog
                    open={!!activeEpaTaskFinding}
                    onClose={() => setActiveEpaTaskFinding(null)}
                    findingId={activeEpaTaskFinding.id}
                    findingText={activeEpaTaskFinding.finding}
                    category={activeEpaTaskFinding.category}
                    appId={appId}
                    onSubmit={handleEpaTaskSubmit}
                  />
                )}

                <Snackbar
                  open={epaTaskSnackbar.open}
                  autoHideDuration={5000}
                  onClose={() => setEpaTaskSnackbar((prev) => ({ ...prev, open: false }))}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                  <Alert
                    onClose={() => setEpaTaskSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={epaTaskSnackbar.severity}
                    sx={{ width: '100%' }}
                  >
                    {epaTaskSnackbar.message}
                  </Alert>
                </Snackbar>
              </Stack>
            );
          })()}

          {/* ═══════════════════════════════════════════════════
              SECURITY PROFILE VIEW
              ═══════════════════════════════════════════════════ */}
          {topView === 'security-profile' && spLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {topView === 'security-profile' && !spLoading && (() => {
            const isExtracting = isSpExtracting;
            const securityProfile = sentinel?.securityProfile;
            return (
              <Stack spacing={3}>
                {/* Action bar */}
                <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {isExtracting && (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.info.main, animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
                        <Typography sx={{ fontSize: 11, color: theme.palette.info.main, fontWeight: 500 }}>
                          {extractionStatuses['security-profile']?.extractionStep === 'analyzing' && extractionStatuses['security-profile']?.totalRepos
                            ? `Security Profile: Scanning repos (${extractionStatuses['security-profile'].completedRepos || 0}/${extractionStatuses['security-profile'].totalRepos})`
                            : extractionStatuses['security-profile']?.extractionStep === 'analyzing' && extractionStatuses['security-profile']?.extractionProgress
                            ? `Security Profile: Analyzing (${extractionStatuses['security-profile'].extractionProgress.completed || 0}/${extractionStatuses['security-profile'].extractionProgress.total || '?'})`
                            : extractionStatuses['security-profile']?.extractionStep === 'checkout' ? 'Security Profile: Checking out code...'
                            : extractionStatuses['security-profile']?.extractionStep === 'storing' ? 'Security Profile: Saving results...'
                            : 'Running Security Profile scan...'}
                        </Typography>
                      </Stack>
                    )}
                    {!isExtracting && securityProfile && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.disabled' }}>
                        <Clock size={13} />
                        <Typography sx={{ fontSize: 11 }}>{new Date(securityProfile.generatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {spError && <Typography sx={{ fontSize: 11, color: 'error.main' }}>{spError}</Typography>}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={isExtracting ? <CircularProgress size={14} color="inherit" /> : securityProfile ? <RefreshCw size={14} /> : <Play size={14} />}
                      onClick={() => setSpEngineDialogOpen(true)}
                      disabled={isExtracting}
                      disableElevation
                      sx={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' } }}
                    >
                      {isExtracting ? 'Starting...' : securityProfile ? 'Re-run Security Profile' : 'Run Security Profile'}
                    </Button>
                  </Stack>
                </Paper>

                {/* Empty state */}
                {!securityProfile && !isExtracting && (
                  <SectionCard>
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <Box sx={{ width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 3, bgcolor: alpha(theme.palette.info.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FileCheck2 size={28} color={theme.palette.info.main} />
                      </Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>No Security Profile Yet</Typography>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3, maxWidth: 440, mx: 'auto' }}>
                        Run an on-demand Security Profile scan to assess authentication, encryption, audit logging, and other security controls across all repositories.
                      </Typography>
                      <Button variant="contained" size="large" startIcon={<Play size={16} />} onClick={() => setSpEngineDialogOpen(true)} disabled={isExtracting} disableElevation
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 4, py: 1.25, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' } }}>
                        Run Security Profile
                      </Button>
                    </Box>
                  </SectionCard>
                )}

                {/* Scanning progress */}
                {isExtracting && !securityProfile && (
                  <SectionCard title="Security Profile" icon={<FileCheck2 size={16} />}>
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Box sx={{ width: 56, height: 56, borderRadius: 3, mx: 'auto', mb: 3, bgcolor: alpha(theme.palette.info.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 2s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.7, transform: 'scale(0.95)' } } }}>
                        <FileCheck2 size={28} color={theme.palette.info.main} />
                      </Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>Security Profile Scan in Progress</Typography>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3 }}>AI is evaluating security controls across all repositories...</Typography>
                      <Stack spacing={1.5} sx={{ maxWidth: 360, mx: 'auto', textAlign: 'left' }}>
                        {([
                          { step: 'checkout', label: 'Checking out repositories', icon: <Layers size={14} /> },
                          { step: 'analyzing', label: 'Scanning & analyzing code', icon: <Brain size={14} /> },
                          { step: 'storing',  label: 'Saving results',            icon: <FileText size={14} /> },
                        ] as const).map(({ step, label, icon }) => {
                          const currentStep = extractionStatuses['security-profile']?.extractionStep || '';
                          const steps = ['checkout', 'analyzing', 'storing'];
                          const isActive = currentStep === step;
                          const isDone = steps.indexOf(step) < steps.indexOf(currentStep);
                          return (
                            <Stack key={step} direction="row" spacing={1.5} alignItems="center" sx={{ opacity: isDone || isActive ? 1 : 0.3 }}>
                              <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: isDone ? alpha('#22c55e', 0.1) : isActive ? alpha(theme.palette.info.main, 0.1) : 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDone ? '#22c55e' : isActive ? theme.palette.info.main : 'text.disabled' }}>
                                {isDone ? <CheckCircle2 size={14} /> : isActive ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : icon}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? 'text.primary' : 'text.secondary' }}>{label}</Typography>
                                {isActive && extractionStatuses['security-profile']?.totalRepos && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography sx={{ fontSize: 10, color: 'text.disabled', mb: 0.5 }}>{extractionStatuses['security-profile'].completedRepos || 0} of {extractionStatuses['security-profile'].totalRepos} repos</Typography>
                                    <LinearProgress variant="determinate" value={((extractionStatuses['security-profile'].completedRepos || 0) / extractionStatuses['security-profile'].totalRepos) * 100} sx={{ height: 3, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1), '& .MuiLinearProgress-bar': { bgcolor: theme.palette.info.main } }} />
                                  </Box>
                                )}
                              </Box>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  </SectionCard>
                )}

                {/* Results */}
                {securityProfile && (
                  <SectionCard title="Security Profile" icon={<FileCheck2 size={16} />}>
                    <SecurityProfileDisplay profile={securityProfile} appName={appName} />
                  </SectionCard>
                )}
              </Stack>
            );
          })()}

          {/* ═══════════════════════════════════════════════════
              REPOSITORY HEALTH VIEW
              ═══════════════════════════════════════════════════ */}
          {topView === 'overview' && (() => {
            // ── Shared helpers ───────────────────────────────
            const ragColor = (rag: string | null | undefined) =>
              rag === 'GREEN' ? '#22c55e' : rag === 'AMBER' ? '#f59e0b' : rag === 'RED' ? '#ef4444' : '#94a3b8';
            const ragLabel = (rag: string | null | undefined) =>
              rag === 'GREEN' ? 'Healthy' : rag === 'AMBER' ? 'Needs attention' : rag === 'RED' ? 'Critical' : 'Unknown';
            const ragSub = (rag: string | null | undefined) =>
              rag === 'GREEN' ? 'Low risk' : rag === 'AMBER' ? 'Elevated risk' : rag === 'RED' ? 'High risk' : '';
            const scoreToRag = (score: number | null | undefined): string => {
              if (score == null) return 'UNKNOWN';
              return score >= 80 ? 'GREEN' : score >= 50 ? 'AMBER' : 'RED';
            };
            const toolStatusIcon = (score: number | null | undefined, connected: boolean) => {
              if (!connected) return { icon: <XCircle size={13} />, color: '#94a3b8' };
              if (score == null) return { icon: <CheckCircle2 size={13} />, color: '#22c55e' };
              if (score >= 80) return { icon: <CheckCircle2 size={13} />, color: '#22c55e' };
              if (score >= 50) return { icon: <AlertTriangle size={13} />, color: '#f59e0b' };
              return { icon: <XCircle size={13} />, color: '#ef4444' };
            };

            // Donut gauge
            const RepoDonut = ({ score, rag }: { score: number; rag: string }) => {
              const color = ragColor(rag);
              const r = 30; const circ = 2 * Math.PI * r;
              const dash = Math.max(0, Math.min(score / 100, 1)) * circ;
              return (
                <Box sx={{ position: 'relative', width: 76, height: 76, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width={76} height={76} style={{ position: 'absolute', transform: 'rotate(-90deg)' }}>
                    <circle cx={38} cy={38} r={r} fill="none" stroke="#e5e7eb" strokeWidth={7} />
                    <circle cx={38} cy={38} r={r} fill="none" stroke={color} strokeWidth={7}
                      strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
                  </svg>
                  <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, lineHeight: 1, color }}>{score}</Typography>
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1.2 }}>/100</Typography>
                  </Box>
                </Box>
              );
            };

            const REPO_TOOL_DEFS = [
              { id: 'sonarqube', label: 'SonarQube' },
              { id: 'blackduck', label: 'Black Duck' },
              { id: 'ghe', label: 'GitLeaks' },
              { id: 'trivy', label: 'Trivy' },
              { id: 'polaris', label: 'Polaris' },
              { id: 'ai_agent', label: 'Delta AI KPIs' },
              { id: 'deep_ai', label: 'Deep AI Analysis' },
            ];

            const DETAIL_TABS = [
              { id: 'sonarqube', label: 'SonarQube', icon: <Bug size={14} /> },
              { id: 'blackduck', label: 'BlackDuck', icon: <Shield size={14} /> },
              { id: 'ghe', label: 'GitLeaks', icon: <Key size={14} /> },
              { id: 'trivy', label: 'Trivy', icon: <Container size={14} /> },
              { id: 'polaris', label: 'Polaris', icon: <ShieldQuestion size={14} /> },
              { id: 'delta-ai', label: 'Delta AI KPIs', icon: <Gauge size={14} /> },
              { id: 'deep-ai', label: 'Deep AI Analysis', icon: <Brain size={14} /> },
            ];

            // Merge scored repos with ALL configured git repos so every data source
            // appears as a card even if it hasn't been scanned yet.
            const scoredRepos = aiKpiScore?.repos ?? [];
            const scoredSlugs = new Set(scoredRepos.map((r) => r.repoSlug));
            const unscoredRepos: AiKpiRepoScore[] = repos
              .filter((r) => !scoredSlugs.has(r.slug))
              .map((r) => ({
                repoSlug: r.slug,
                criticality: 'MEDIUM',
                repoScore: null,
                repoRag: null,
                toolScores: {},
                connectedTools: [],
                kpiScores: {},
                topFixes: [],
                ragTransition: null,
              }));
            const kpiRepos = [...scoredRepos, ...unscoredRepos];
            // Filter repos by search term, then sort
            const repoSearchLower = repoSearch.trim().toLowerCase();
            const filteredRepos = repoSearchLower
              ? kpiRepos.filter((r) => r.repoSlug?.toLowerCase().includes(repoSearchLower))
              : kpiRepos;
            const sortedRepos = [...filteredRepos].sort((a, b) =>
              repoSortBy === 'health'
                ? (b.repoScore ?? 0) - (a.repoScore ?? 0)  // descending (default)
                : (a.repoScore ?? 0) - (b.repoScore ?? 0), // ascending
            );
            const hasAiData = !!(aiKpiScore?.repos?.some(r => r.toolScores?.ai_agent != null));
            const lastAiScanDate = aiKpiScore?.analyzedAt;

            return (
              <Stack spacing={2} sx={{ pt: 2 }}>
                {/* Header bar */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Repository Health</Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                      — Repository-level signals contributing to the Project Health Score
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                    {/* Repository search */}
                    <TextField
                      size="small"
                      placeholder="Search repositories…"
                      value={repoSearch}
                      onChange={(e) => setRepoSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search size={14} color={theme.palette.text.secondary} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: 210,
                        '& .MuiOutlinedInput-root': {
                          fontSize: 13,
                          height: 32,
                          borderRadius: 2,
                        },
                      }}
                    />
                    {/* Repo count + sort */}
                    {kpiRepos.length > 0 && (
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', borderLeft: '1px solid', borderColor: 'divider', pl: 1.5 }}>
                        {sortedRepos.length !== kpiRepos.length
                          ? `${sortedRepos.length} / ${kpiRepos.length} ${kpiRepos.length === 1 ? 'repository' : 'repositories'}`
                          : `${kpiRepos.length} ${kpiRepos.length === 1 ? 'repository' : 'repositories'}`}
                      </Typography>
                    )}
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Sort by health score</Typography>
                      <Chip
                        label={repoSortBy === 'health' ? '↓ Highest first' : '↑ Lowest first'}
                        size="small"
                        onClick={() => setRepoSortBy(repoSortBy === 'health' ? 'name' : 'health')}
                        sx={{ fontSize: 12, height: 26, fontWeight: 600, cursor: 'pointer', bgcolor: alpha(theme.palette.primary.main, 0.07), color: 'primary.main' }}
                      />
                    </Stack>
                  </Stack>
                </Stack>



                {/* Repo cards */}
                {sortedRepos.length === 0 && kpiRepos.length === 0 && (
                  <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                      No repository score data yet. Run a scan to populate scores.
                    </Typography>
                  </Paper>
                )}
                {sortedRepos.length === 0 && kpiRepos.length > 0 && (
                  <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                      No repositories match "<strong>{repoSearch}</strong>".
                    </Typography>
                  </Paper>
                )}

                {sortedRepos.map((repo) => {
                  const repoConf = repos.find(r => r.slug === repo.repoSlug);
                  const score = Math.round(repo.repoScore ?? 0);
                  const rag = repo.repoRag ?? scoreToRag(repo.repoScore);
                  const color = ragColor(rag);
                  const isExpanded = repoDetailSlug === repo.repoSlug;
                  const repoScore = aiKpiScore?.repos?.find(r => r.repoSlug === repo.repoSlug);

                  return (
                    <Paper
                      key={repo.repoSlug}
                      elevation={0}
                      sx={{
                        borderRadius: 2, border: '1px solid',
                        borderColor: isExpanded ? theme.palette.primary.main : 'divider',
                        boxShadow: isExpanded ? `0 2px 16px ${alpha(theme.palette.primary.main, 0.1)}` : 'none',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        overflow: 'hidden',
                      }}
                    >
                      {/* ── Card header row ──
                          MUI Grid (spacing=2): width=parent+16px, marginLeft=-8px → right side
                          extends 8px beyond parent. Items have paddingLeft=8px only (no paddingRight).
                          So right content sits 8px closer to border. Fix: pr=3.5 (28px) compensates
                          for Grid's extra 8px right width. Left stays pl=2.5 (20px), net equal 20px. */}
                      <Box sx={{ pt: 2.5, pl: 2.5, pb: 2.5, pr: 3.5 }}>
                      <Grid container spacing={2} alignItems="center">
                        {/* Col 1: repo info */}
                        <Grid item xs={12} md={3}>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Layers size={18} color={theme.palette.primary.main} />
                            </Box>
                            <Box>
                              {/* Repo name — links to repo root */}
                              {repoConf?.url ? (
                                <Link
                                  href={repoConf.url.replace(/\.git$/, '')}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  underline="hover"
                                  sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: 'text.primary', '&:hover': { color: 'primary.main' } }}
                                >
                                  {repo.repoSlug}
                                </Link>
                              ) : (
                                <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>{repo.repoSlug}</Typography>
                              )}
                              {repoConf?.category && (
                                <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{repoConf.category}</Typography>
                              )}
                              {/* Branch — links to that branch on the remote */}
                              {repoConf?.branch && (
                                repoConf?.url ? (
                                  <Link
                                    href={`${repoConf.url.replace(/\.git$/, '')}/tree/${repoConf.branch}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    sx={{ fontSize: 10, color: 'text.disabled', display: 'flex', alignItems: 'center', gap: 0.4, width: 'fit-content', '&:hover': { color: 'primary.main' } }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <GitBranch size={10} style={{ flexShrink: 0 }} />
                                    {repoConf.branch}
                                  </Link>
                                ) : (
                                  <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{repoConf.branch}</Typography>
                                )
                              )}
                            </Box>
                          </Stack>
                        </Grid>

                        {/* Col 2: donut + label */}
                        <Grid item xs={12} md={2.5}>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <RepoDonut score={score} rag={rag} />
                            <Box>
                              <Typography sx={{ fontSize: 13, fontWeight: 700, color, lineHeight: 1.3 }}>{ragLabel(rag)}</Typography>
                              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{ragSub(rag)}</Typography>
                            </Box>
                          </Stack>
                        </Grid>

                        {/* Col 3: tool status grid */}
                        <Grid item xs={12} md={5}>
                          <Grid container rowSpacing={0.75} columnSpacing={2}>
                            {REPO_TOOL_DEFS.map((toolDef) => {
                              const toolId = (toolDef.id === 'deep_ai' || toolDef.id === 'polaris') ? null : toolDef.id;
                              const rawScore = toolId ? repo.toolScores?.[toolId] : null;
                              const connected = toolDef.id === 'polaris'
                                ? !!(pipelineStatus?.tools?.polaris)
                                : toolId
                                  ? (repo.connectedTools?.includes(toolId) ?? false)
                                  : !!(sentinel);
                              const { icon, color: icolor } = toolStatusIcon(rawScore, connected);
                              return (
                                <Grid item xs={4} key={toolDef.id}>
                                  <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Box sx={{ color: icolor, display: 'flex', flexShrink: 0 }}>{icon}</Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{toolDef.label}</Typography>
                                  </Stack>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Grid>

                        {/* Col 4: toggle button */}
                        <Grid item xs={12} md={1.5} sx={{ textAlign: { md: 'right' } }}>
                          <Button
                            size="small"
                            endIcon={<ChevronDown size={13} style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
                            onClick={() => {
                              if (isExpanded) {
                                setRepoDetailSlug(null);
                                setRepoDetailTab(0);
                              } else {
                                setRepoDetailSlug(repo.repoSlug);
                                setRepoDetailTab(0);
                              }
                            }}
                            sx={{ textTransform: 'none', fontWeight: 600, color: isExpanded ? 'primary.main' : 'text.secondary', fontSize: 13, whiteSpace: 'nowrap' }}
                          >
                            {isExpanded ? 'Collapse' : 'View repository'}
                          </Button>
                        </Grid>
                      </Grid>
                      </Box>

                      {/* ── Inline expanded detail ── */}
                      <Collapse in={isExpanded} unmountOnExit>
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                          {/* Tab strip */}
                          <Tabs
                            value={repoDetailTab}
                            onChange={(_, v) => setRepoDetailTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                              px: 2.5,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 44, py: 0 },
                              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
                            }}
                          >
                            {DETAIL_TABS.map((tab) => {
                              const toolId = tab.id === 'delta-ai' ? 'ai_agent' : (tab.id === 'deep-ai' || tab.id === 'polaris') ? null : tab.id;
                              const toolScore = tab.id === 'deep-ai'
                                ? (deepAiRepoResults[repo.repoSlug]?.securityScore?.overall ?? null)
                                : (toolId ? repoScore?.toolScores?.[toolId] : null);
                              const isConnected = toolId ? (repoScore?.connectedTools?.includes(toolId) ?? false) : true;
                              return (
                                <Tab
                                  key={tab.id}
                                  icon={<Box sx={{ display: 'flex', mr: 0.75 }}>{tab.icon}</Box>}
                                  iconPosition="start"
                                  label={
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                      <span>{tab.label}</span>
                                      {toolScore != null && toolScore > 0 && (
                                        <Chip label={Math.round(toolScore)} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                                          bgcolor: alpha(toolScore >= 80 ? '#22c55e' : toolScore >= 50 ? '#f59e0b' : '#ef4444', 0.1),
                                          color: toolScore >= 80 ? '#22c55e' : toolScore >= 50 ? '#f59e0b' : '#ef4444' }} />
                                      )}
                                      {!isConnected && toolId && (
                                        <Chip label="N/C" size="small" sx={{ height: 16, fontSize: '0.5rem', bgcolor: alpha(theme.palette.text.disabled, 0.1), color: 'text.disabled' }} />
                                      )}
                                    </Stack>
                                  }
                                />
                              );
                            })}
                          </Tabs>

                          {/* Tab content — pr: 3.5 compensates for MUI Grid's 8px right overflow
                              so right margin equals left margin (both visually 20px / 2.5 units). */}
                          <Box sx={{ pt: 2.5, pl: 2.5, pb: 2.5, pr: 3.5 }}>
                            {/* Tabs 0–3: SonarQube / BlackDuck / GitLeaks / Trivy */}
                            {repoDetailTab < 4 && (
                              <ToolTabContent
                                tabId={DETAIL_TABS[repoDetailTab]?.id}
                                appId={appId}
                                selectedRepo={repo.repoSlug}
                                repoScore={repoScore}
                                pipelineStatus={pipelineStatus}
                              />
                            )}

                            {/* Tab 4: Polaris (per-repo slice of the application report) */}
                            {repoDetailTab === 4 && (
                              <PolarisRepoTab appId={appId} repoSlug={repo.repoSlug} />
                            )}

                            {/* Tab 5: Delta AI KPIs (outer Box already provides p: 2.5) */}
                            {repoDetailTab === 5 && (
                              <Stack spacing={3}>
                                <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                    {aiScanRunning && aiScanProgress && (
                                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mr: 1 }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#0B6BCB', animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
                                        <Typography sx={{ fontSize: 11, color: '#0B6BCB', fontWeight: 500 }}>
                                          {aiScanProgress.current_pass ? `Pass ${aiScanProgress.passes_completed + 1}/4: ${aiScanProgress.current_pass}` : `AI scan: ${aiScanProgress.passes_completed}/4 passes`}
                                        </Typography>
                                      </Stack>
                                    )}
                                    {!aiScanRunning && lastAiScanDate && (
                                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.disabled', mr: 1 }}>
                                        <Clock size={13} />
                                        <Typography sx={{ fontSize: 11 }}>{new Date(lastAiScanDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                                      </Stack>
                                    )}
                                  </Stack>
                                  <Button variant="contained" size="small"
                                    startIcon={aiScanRunning ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
                                    onClick={() => handleTriggerAiScan(repo.repoSlug)} disabled={aiScanRunning} disableElevation
                                    sx={{ textTransform: 'none', fontWeight: 700, background: '#0B6BCB', '&:hover': { background: '#185ABC' }, '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 } }}>
                                    {aiScanRunning ? 'Scanning...' : hasAiData ? 'Re-scan' : 'Run AI Scan'}
                                  </Button>
                                </Paper>
                                {!aiScanRunning && hasAiData && repoScore && (
                                  <DeltaAiKpisTab
                                    toolScore={repoScore.toolScores?.ai_agent ?? null}
                                    kpiScores={repoScore.kpiScores}
                                    topFixes={repoScore.topFixes}
                                    loading={false} scanning={false} scanProgress={null}
                                  />
                                )}
                                {!aiScanRunning && !hasAiData && (
                                  <Box sx={{ py: 6, textAlign: 'center' }}>
                                    <Box sx={{ width: 64, height: 64, borderRadius: 3, mx: 'auto', mb: 3, bgcolor: alpha('#0B6BCB', 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Gauge size={32} color="#0B6BCB" />
                                    </Box>
                                    <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>Delta AI KPIs</Typography>
                                    <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 3, maxWidth: 440, mx: 'auto' }}>
                                      Run a 4-pass AI-powered analysis to generate advanced KPIs including SBOM coverage, dependency confusion risk, and reachable vulnerability analysis.
                                    </Typography>
                                    <Button variant="contained" size="large" startIcon={<Play size={16} />} onClick={() => handleTriggerAiScan(repo.repoSlug)} disableElevation
                                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 4, py: 1.25, background: '#0B6BCB', '&:hover': { background: '#185ABC' }, '&.Mui-disabled': { background: '#0B6BCB', color: '#fff', opacity: 0.65 } }}>
                                      Run AI Scan
                                    </Button>
                                  </Box>
                                )}
                              </Stack>
                            )}

                            {/* Tab 6: Deep AI Analysis (outer Box already provides p: 2.5) */}
                            {repoDetailTab === 6 && (
                              <Box>
                                <DeepAiAnalysis
                                  sentinel={sentinel}
                                  appId={appId}
                                  appName={appName}
                                  extractionStatus={extractionStatuses['deep-ai']}
                                  isSectionExtracting={isDeepAiExtracting}
                                  onRunAnalysis={() => handleOpenRepoSelectionDialog('complete')}
                                  isRunning={rescanning}
                                  lastScanDate={lastScanDate}
                                  onWorkflow={() => setWorkflowDialogOpen(true)}
                                  selectedRepo={repo.repoSlug}
                                  onRepoChange={setSelectedRepo}
                                  repoProgress={extractionStatuses['deep-ai']?.repoProgress}
                                  repos={repos}
                                  repoResults={deepAiRepoResults}
                                  repoLoading={deepAiRepoLoading}
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}


              </Stack>
            );
          })()}

          {/* ═══════════════════════════════════════════════════
              DIALOGS
              ═══════════════════════════════════════════════════ */}
          <Dialog open={confirmRescanOpen} onClose={() => setConfirmRescanOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Re-run security scan?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {sentinel?.extractedAt && (
                  <>The last scan for <strong>{appName}</strong> was performed on{' '}
                    <strong>
                      {new Date(sentinel.extractedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </strong>, less than 3 months ago. Do you want to rerun the scan?
                  </>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmRescanOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button
                variant="contained"
                sx={{ textTransform: 'none' }}
                onClick={() => { setConfirmRescanOpen(false); setRepoSelectionDialogOpen(true); }}
              >
                Yes, rerun scan
              </Button>
            </DialogActions>
          </Dialog>

          <RepoSelectionDialog
            open={repoSelectionDialogOpen}
            onClose={() => setRepoSelectionDialogOpen(false)}
            repos={repos.map((r) => ({
              repoSlug: r.slug,
              repoName: r.slug,
              repoUrl: r.url,
              category: r.category,
              extractedAt: sentinel?.extractedAt,
            }))}
            defaultEngine={defaultEngine}
            onConfirm={(selected, agentMode) => {
              setRepoSelectionDialogOpen(false);
              handleRescan(pendingScanType, selected, agentMode);
            }}
            isLoading={rescanning}
          />

          <ScanEngineDialog
            open={epaEngineDialogOpen}
            onClose={() => setEpaEngineDialogOpen(false)}
            onConfirm={(agentMode) => {
              setEpaEngineDialogOpen(false);
              handleOnDemandScan('epa', agentMode);
            }}
            title={sentinel?.epaSummary ? 'Re-run EPA Scan' : 'Run EPA Scan'}
            description="Choose the AI engine for this Enterprise Privacy Assessment scan."
            defaultEngine={defaultEngine}
            accentColor="#0B6BCB"
            confirmLabel={sentinel?.epaSummary ? 'Re-run Scan' : 'Start Scan'}
          />

          <ScanEngineDialog
            open={spEngineDialogOpen}
            onClose={() => setSpEngineDialogOpen(false)}
            onConfirm={(agentMode) => {
              setSpEngineDialogOpen(false);
              handleOnDemandScan('security-profile', agentMode);
            }}
            title={sentinel?.securityProfile ? 'Re-run Security Profile' : 'Run Security Profile'}
            description="Choose the AI engine for this Security Profile scan."
            defaultEngine={defaultEngine}
            accentColor="#3b82f6"
            confirmLabel={sentinel?.securityProfile ? 'Re-run Scan' : 'Start Scan'}
          />

          <WorkflowGeneratorDialog
            open={workflowDialogOpen}
            onClose={() => setWorkflowDialogOpen(false)}
            appId={appId}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ── Tool Tab Content Router (self-loading per tab) ────────────
function ToolTabContent({
  tabId,
  appId,
  selectedRepo,
  repoScore,
  pipelineStatus,
}: {
  tabId: string;
  appId: string;
  selectedRepo: string | null;
  repoScore?: AiKpiRepoScore;
  pipelineStatus?: PipelineStatus | null;
}) {
  // Cache fetched tab data by appId+tool+repo to avoid re-fetch on tab switch
  const cacheRef = useRef<Record<string, ToolReportDetail>>({});
  const [tabData, setTabData] = useState<ToolReportDetail | null>(null);
  const [tabLoading, setTabLoading] = useState(false);

  const pipelineToolStatus = pipelineStatus?.tools?.[tabId]?.status;

  // Shared fetch helper – accepts an optional guard so the effect-cleanup case can cancel.
  const loadTab = useCallback((isCancelled: () => boolean = () => false) => {
    const cacheKey = `${appId}:${tabId}:${selectedRepo || ''}`;
    setTabLoading(true);
    getToolReportDetail(appId, tabId, selectedRepo || undefined)
      .then((detail) => {
        if (!isCancelled()) {
          cacheRef.current[cacheKey] = detail;
          setTabData(detail);
        }
      })
      .catch(() => {
        if (!isCancelled()) setTabData(null);
      })
      .finally(() => {
        if (!isCancelled()) setTabLoading(false);
      });
  }, [appId, tabId, selectedRepo]);

  // Fetch data when tab, repo, or pipeline completion changes
  useEffect(() => {
    const cacheKey = `${appId}:${tabId}:${selectedRepo || ''}`;

    // Use cache if available
    if (cacheRef.current[cacheKey]) {
      setTabData(cacheRef.current[cacheKey]);
      return;
    }

    let cancelled = false;
    loadTab(() => cancelled);
    return () => { cancelled = true; };
  }, [appId, tabId, selectedRepo, loadTab]);

  // Invalidate cache when pipeline completes (status changes from running → completed/failed)
  const prevPipelineRef = useRef(pipelineStatus?.status);
  useEffect(() => {
    const prev = prevPipelineRef.current;
    const curr = pipelineStatus?.status;
    prevPipelineRef.current = curr;
    if (prev === 'running' && (curr === 'completed' || curr === 'failed')) {
      // Clear cache so tabs re-fetch fresh data
      cacheRef.current = {};
      // Re-trigger fetch for current tab
      loadTab();
    }
  }, [pipelineStatus?.status, appId, tabId, selectedRepo, loadTab]);

  // Use the API toolScore when available; fall back to the aiKpiScore data for this
  // repo (same DynamoDB record, fetched at page-load) so the tab content is always
  // consistent with the badge even when getToolReportDetail can't resolve the repo.
  const apiToolScore: number | null = tabData?.toolScore ?? null;
  const fallbackScore: number | null = (repoScore?.toolScores as Record<string, number> | undefined)?.[tabId] ?? null;
  const toolScore = apiToolScore ?? fallbackScore;

  // KPI prefixes per tool — mirrors TOOL_KPI_PREFIXES in sentinelStorageService.js
  const TOOL_KPI_PREFIXES: Record<string, string[]> = {
    sonarqube: ['sonar_'],
    blackduck: ['bd_'],
    ghe: ['secrets_detected', 'branch_protected', 'pipeline_success', 'stale_prs'],
    trivy: ['image_'],
  };
  // Use API kpiScores when present; fall back to the per-repo kpiScores already
  // fetched at page-load (aiKpiScore) filtered to this tool's prefix.
  const apiKpiScores = tabData?.kpiScores ?? {};
  const fallbackKpiScores = Object.fromEntries(
    Object.entries((repoScore?.kpiScores as Record<string, number> | undefined) ?? {})
      .filter(([k]) => (TOOL_KPI_PREFIXES[tabId] ?? []).some((p) => k.startsWith(p))),
  );
  const kpiScores = Object.keys(apiKpiScores).length > 0 ? apiKpiScores : fallbackKpiScores;
  const topFixes = tabData?.topFixes ?? [];
  const rawReport = tabData?.rawReport ?? null;

  // Trend data from API; fallback to single data point using the tool's actual scan date
  let toolTrend = tabData?.trendData ?? [];
  if (toolTrend.length === 0 && toolScore != null) {
    // Prefer the tool's own analyzedAt date, then the pipeline triggeredAt, then today
    const scanDate = tabData?.analyzedAt || pipelineStatus?.triggeredAt || new Date().toISOString();
    toolTrend = [{
      date: scanDate,
      score: Math.round(toolScore),
      label: new Date(scanDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    }];
  }

  const props = { toolScore, kpiScores, topFixes, trendData: toolTrend, loading: tabLoading, pipelineToolStatus };

  switch (tabId) {
    case 'sonarqube': return <SonarQubeTab {...props} toolReport={rawReport} />;
    case 'blackduck': return <BlackDuckTab {...props} toolReport={rawReport} />;
    case 'ghe': return <GitLeaksTab {...props} toolReport={rawReport} />;
    case 'trivy': return <TrivyTab {...props} toolReport={rawReport} />;
    default: return null;
  }
}

// ── Meta Chip ─────────────────────────────────────────────────
function MetaChip({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography sx={{ fontSize: 10, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase' }}>
        {label}:
      </Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500 }}>
        {value}
      </Typography>
    </Stack>
  );
}

// ── Repo Radio Bar ────────────────────────────────────────────
interface RepoRadioBarProps {
  repos: { slug: string; branch: string; url?: string; category?: string }[];
  selectedRepo: string | null;
  onChange: (slug: string) => void;
  repoProgress?: Record<string, any>;
  isScanning?: boolean;
}

export function RepoRadioBar({ repos, selectedRepo, onChange, repoProgress, isScanning }: RepoRadioBarProps) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        overflowX: 'auto',
        pb: 0.5,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': { borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.12) },
      }}
    >
      {repos.map((repo, idx) => {
        const isSelected = selectedRepo === repo.slug;
        const isRepoScanning = isScanning && (
          repoProgress ? ['running', 'queued', 'pending'].includes(repoProgress[repo.slug]?.status) : false
        );
        return (
          <Box
            key={repo.slug}
            component="label"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: 5,
              border: '1px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
              bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.15s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, isSelected ? 0.15 : 0.06),
              },
            }}
          >
            <MuiRadio
              size="small"
              checked={isSelected}
              onChange={() => onChange(repo.slug)}
              sx={{
                p: 0,
                color: isSelected ? 'primary.main' : 'text.disabled',
                '&.Mui-checked': { color: 'primary.main' },
              }}
            />
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: 'text.disabled',
                minWidth: 18,
                userSelect: 'none',
              }}
            >
              R{idx + 1}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: isSelected ? 700 : 500,
                color: isSelected ? 'text.primary' : 'text.secondary',
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {repo.slug}
            </Typography>
            <Chip
              label={repo.branch}
              size="small"
              sx={{
                height: 16,
                fontSize: '0.55rem',
                fontWeight: 600,
                bgcolor: alpha(theme.palette.info.main, 0.08),
                color: 'info.main',
                borderRadius: 1,
                pointerEvents: 'none',
              }}
            />
            {isRepoScanning && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: theme.palette.info.main,
                  flexShrink: 0,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                }}
              />
            )}
            {repo.url && (
              <Tooltip title="Open in GitHub" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => { e.preventDefault(); window.open(repo.url, '_blank'); }}
                  sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                >
                  <ExternalLink size={11} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
